import React, { useEffect, useRef, useState } from 'react';
import { CellValue } from '../api/service';

export type EditableCellType = 'string' | 'number' | 'boolean' | 'date';

export interface EditableCellProps {
  value: CellValue;
  type?: EditableCellType;
  editable: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCommit: (newValue: CellValue) => Promise<void>;
  onCancel: () => void;
}

/**
 * Single-cell inline editor used inside the existing /master-data table.
 * Click → input (or select for boolean). Enter / blur → commit. Escape → cancel.
 * On commit failure the previous value is restored by the parent.
 */
export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  type = 'string',
  editable,
  isEditing,
  onStartEdit,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState<string>(value == null ? '' : String(value));
  const [saving, setSaving] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const committedRef = useRef(false);

  // Reset draft and commit guard in a way that avoids a double-commit race:
  // - When isEditing becomes FALSE (modal cancel / parent clears editing): clear committedRef
  //   so the cell can be re-edited immediately.
  // - When isEditing becomes TRUE: also reset to accept a fresh edit.
  // Crucially, setDraft runs unconditionally so the displayed value stays in sync with `value`.
  useEffect(() => {
    if (!isEditing) {
      committedRef.current = false;
      setCommitError(null);
    }
    setDraft(value == null ? '' : String(value));
  }, [isEditing, value]);

  const display = value == null || value === '' ? '\u00A0' : String(value);

  if (!editable || !isEditing) {
    return (
      <span
        onClick={(e) => {
          if (!editable) return;
          e.stopPropagation();
          onStartEdit();
        }}
        title={editable ? 'Click to edit' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          minHeight: '1.2em',
          cursor: editable ? 'text' : 'default',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display}
        </span>
        <span
          aria-hidden
          style={{
            color: editable ? '#6b7280' : '#9ca3af',
            fontSize: '0.8em',
            lineHeight: 1,
            flexShrink: 0,
          }}
          title={editable ? 'Editable' : 'Read only'}
        >
          {editable ? '✎' : '🔒'}
        </span>
      </span>
    );
  }

  const commit = async () => {
    if (committedRef.current || saving) return;
    committedRef.current = true;
    const original = value == null ? '' : String(value);
    if (draft === original) {
      onCancel();
      return;
    }
    let parsed: CellValue = draft;
    if (type === 'number') {
      if (draft === '') {
        parsed = null;
      } else {
        const n = Number(draft);
        if (Number.isNaN(n)) {
          committedRef.current = false;
          onCancel();
          return;
        }
        parsed = n;
      }
    } else if (type === 'boolean') {
      parsed = draft === 'true';
    } else if (draft === '') {
      parsed = null;
    }
    setSaving(true);
    try {
      await onCommit(parsed);
      // committedRef was already set to true at the top of commit() as a re-entry guard.
      // It remains true here; the useEffect resets it if the user cancels the modal.
    } catch (e) {
      setSaving(false);
      if (e instanceof Error && e.message === 'cancelled') {
        // User dismissed the confirm dialog — reset committedRef so the cell can be re-edited.
        committedRef.current = false;
        return;
      }
      // Keep committedRef.current = true to prevent a subsequent blur from re-entering commit().
      // The cell stays in error state; useEffect resets committedRef when isEditing → false.
      setCommitError(e instanceof Error ? e.message : 'Save failed');
      return;
    }
    setSaving(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
      // Explicitly blur so the onBlur handler fires while committedRef is still true.
      // Without this, React may fire onBlur after re-rendering the span (on unmount),
      // potentially re-entering commit() if committedRef was reset by the useEffect.
      (e.currentTarget as HTMLElement).blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      committedRef.current = true;
      onCancel();
    }
  };

  if (type === 'boolean') {
    return (
      <select
        autoFocus
        className="input is-small"
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%' }}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  return (
    <>
      <input
        autoFocus
        className={`input is-small${commitError ? ' is-danger' : ''}`}
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        value={draft}
        disabled={saving}
        onChange={(e) => { setDraft(e.target.value); setCommitError(null); }}
        onBlur={() => void commit()}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%' }}
        title={commitError ?? undefined}
      />
      {commitError && (
        <span style={{ color: '#dc3545', fontSize: '0.75em', display: 'block' }}>
          {commitError}
        </span>
      )}
    </>
  );
};

export default EditableCell;
